import { Exchange } from "@urql/core";
import { onPush, pipe, share } from "wonka";

/**
 * Tracks mutations that are sent but not answered yet, including those the `offlineExchange` replays from its queue.
 * Placed between the `offlineExchange` and the `fetchExchange`, so queued mutations do not count.
 *
 * The playlist sync waits for them: a query started after a mutation lies above its optimistic result in graphcache,
 * so the list of all ids would hide a playlist whose creation is still on the way.
 */
export function trackMutationsInFlight() {
  const inFlight = new Set<number>();
  const idleListeners = new Set<() => void>();
  const settle = (key: number) => {
    if (inFlight.delete(key) && inFlight.size === 0) {
      idleListeners.forEach((l) => l());
    }
  };

  const exchange: Exchange =
    ({ forward }) =>
    (operations$) =>
      pipe(
        forward(
          pipe(
            operations$,
            onPush((op) => {
              if (op.kind === "mutation") {
                inFlight.add(op.key);
              } else if (op.kind === "teardown") {
                settle(op.key);
              }
            }),
            share,
          ),
        ),
        onPush((result) => {
          if (result.operation.kind === "mutation") {
            settle(result.operation.key);
          }
        }),
      );

  /** Resolves once no mutation is in flight, at the latest after `timeoutMs`. */
  const whenIdle = (timeoutMs: number): Promise<void> =>
    inFlight.size === 0
      ? Promise.resolve()
      : new Promise((resolve) => {
          const done = () => {
            clearTimeout(timeout);
            idleListeners.delete(done);
            resolve();
          };
          const timeout = setTimeout(done, timeoutMs);
          idleListeners.add(done);
        });

  return { exchange, whenIdle };
}

export const mutationsInFlight = trackMutationsInFlight();

/** Exposed on `window` in dev and test mode, so Cypress can wait until all changes reached the server. */
export interface SongshipTestHooks {
  songshipWhenMutationsIdle: () => Promise<void>;
}
